// src/services/savedSpots.ts

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  Unsubscribe,
} from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';

import { auth, db } from '../lib/firebase';
import { ensureJsAuthFromNative } from './authBridge';
import type { SavedSpotKind, SavedSpotProfileRecord } from '../types';

const SAVED_SPOTS_COLLECTION = 'savedSpots';

type AnyRecord = Record<string, any>;

async function ensureAuthedForSavedSpotWrite(label: string) {
  try {
    const isNative = (() => {
      try {
        return Capacitor.isNativePlatform();
      } catch {
        return false;
      }
    })();

    if (!isNative) return;
    if (auth.currentUser?.uid) return;

    await Promise.race([
      ensureJsAuthFromNative(),
      new Promise((resolve) => setTimeout(resolve, 2500)),
    ]);
  } catch (e) {
    console.warn(`[savedSpots] ensureAuthedForSavedSpotWrite(${label}) failed`, e);
  }
}

export function normalizeSavedSpotKind(value: unknown): SavedSpotKind {
  const v = String(value ?? 'favorite')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');

  if (v === 'fish' || v === 'fishing' || v === 'fishspot' || v === 'fish_spot' || v === 'fishing_spot') {
    return 'fish';
  }

  if (v === 'food' || v === 'restaurant' || v === 'eat' || v === 'meal' || v === 'foodspot' || v === 'food_spot') {
    return 'food';
  }

  if (v === 'launch' || v === 'boat' || v === 'ramp' || v === 'boat_launch' || v === 'boatlaunch') {
    return 'launch';
  }

  if (v === 'sunset' || v === 'view' || v === 'scenic' || v === 'photo' || v === 'picture') {
    return 'sunset';
  }

  return 'favorite';
}

function inferSavedSpotKindFromLabel(value: unknown): SavedSpotKind | null {
  const v = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');

  if (!v) return null;
  if (v === 'fish' || v === 'fish_spot' || v === 'fishing_spot' || v.includes('fish_spot')) return 'fish';
  if (v === 'food' || v === 'food_spot' || v === 'restaurant' || v.includes('food_spot')) return 'food';
  if (v === 'launch' || v === 'boat_launch' || v.includes('launch')) return 'launch';
  if (v === 'sunset' || v === 'scenic' || v.includes('sunset')) return 'sunset';
  return null;
}

function resolveSavedSpotKind(raw: any): SavedSpotKind {
  const explicit = normalizeSavedSpotKind(raw?.kind ?? raw?.type ?? raw?.category ?? raw?.spotType);

  // Older/buggy clients sometimes persisted fish/food spots as kind: 'favorite'
  // while keeping the default label as the name/title. Recover those so every
  // platform renders the intended marker instead of a star.
  if (explicit !== 'favorite') return explicit;

  return (
    inferSavedSpotKindFromLabel(raw?.kindLabel) ??
    inferSavedSpotKindFromLabel(raw?.label) ??
    inferSavedSpotKindFromLabel(raw?.name) ??
    inferSavedSpotKindFromLabel(raw?.title) ??
    explicit
  );
}

function toMillis(value: any): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value && typeof value.toMillis === 'function') {
    try {
      const n = value.toMillis();
      if (Number.isFinite(n)) return n;
    } catch {}
  }
  if (value && typeof value.toDate === 'function') {
    try {
      const d = value.toDate();
      const n = d instanceof Date ? d.getTime() : NaN;
      if (Number.isFinite(n)) return n;
    } catch {}
  }
  if (value && Number.isFinite(Number(value.seconds))) return Number(value.seconds) * 1000;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function normalizeSavedSpot(raw: any, fallbackId?: string): SavedSpotProfileRecord | null {
  const lat = Number(raw?.lat ?? raw?.position?.lat);
  const lng = Number(raw?.lng ?? raw?.lon ?? raw?.position?.lng ?? raw?.position?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const kind = resolveSavedSpotKind(raw);
  const id = String(raw?.id || fallbackId || '').trim();
  if (!id) return null;

  const name = String(raw?.name || raw?.title || kind).trim() || kind;
  const note = typeof raw?.note === 'string' ? raw.note.trim() : '';
  const createdAt = toMillis(raw?.clientCreatedAt ?? raw?.createdAt ?? raw?.savedAt ?? raw?.timestamp);
  if (!createdAt) return null;

  return {
    id,
    name,
    kind,
    lat,
    lng,
    note,
    createdAt,
    clientCreatedAt: createdAt,
  } as SavedSpotProfileRecord;
}

export function normalizeSavedSpots(input: any): SavedSpotProfileRecord[] {
  if (!Array.isArray(input)) return [];

  const seen = new Set<string>();
  const out: SavedSpotProfileRecord[] = [];

  input.forEach((raw, idx) => {
    const spot = normalizeSavedSpot(raw, `saved_${idx}`);
    if (!spot) return;

    const key = String(spot.id || '').trim();

    if (seen.has(key)) return;
    seen.add(key);
    out.push(spot);
  });

  return out.sort((a, b) => Number((b as any).clientCreatedAt || b.createdAt || 0) - Number((a as any).clientCreatedAt || a.createdAt || 0));
}


async function removeLegacyProfileSavedSpot(userId: string, spotId: string): Promise<void> {
  const uid = String(userId || '').trim();
  const id = String(spotId || '').trim();
  if (!uid || !id) return;

  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return;

  const legacySavedSpots = (snap.data() as AnyRecord)?.savedSpots;
  if (!Array.isArray(legacySavedSpots) || legacySavedSpots.length === 0) return;

  const nextLegacySavedSpots = legacySavedSpots.filter((raw) => {
    const rawId = String(raw?.id || '').trim();
    return rawId !== id;
  });

  if (nextLegacySavedSpots.length === legacySavedSpots.length) return;
  await updateDoc(userRef, { savedSpots: nextLegacySavedSpots });
}

function savedSpotPayload(spotInput: AnyRecord) {
  const spot = normalizeSavedSpot(spotInput, spotInput?.id);
  if (!spot) throw new Error('Invalid saved spot');

  const clientCreatedAt = Number((spot as any).clientCreatedAt || spot.createdAt || 0);
  if (!Number.isFinite(clientCreatedAt) || clientCreatedAt <= 0) {
    throw new Error('Saved spot requires a stable clientCreatedAt');
  }

  return {
    id: spot.id,
    name: spot.name,
    kind: spot.kind,
    type: spot.kind,
    lat: spot.lat,
    lng: spot.lng,
    note: spot.note || '',
    createdAt: clientCreatedAt,
    clientCreatedAt,
    serverUpdatedAt: serverTimestamp(),
  };
}

export function subscribeToSavedSpots(
  userId: string,
  onChange: (spots: SavedSpotProfileRecord[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const uid = String(userId || '').trim();
  if (!uid) {
    onChange([]);
    return () => {};
  }

  const q = query(collection(db, 'users', uid, SAVED_SPOTS_COLLECTION), orderBy('clientCreatedAt', 'desc'));

  return onSnapshot(
    q,
    (snap) => {
      const next = snap.docs
        .map((d) => normalizeSavedSpot({ id: d.id, ...d.data() }, d.id))
        .filter(Boolean) as SavedSpotProfileRecord[];
      onChange(normalizeSavedSpots(next));
    },
    (err) => {
      console.error('[savedSpots] subscribeToSavedSpots failed', err);
      onError?.(err);
      onChange([]);
    },
  );
}

export async function saveSavedSpot(userId: string, spotInput: AnyRecord): Promise<void> {
  const uid = String(userId || '').trim();
  if (!uid) throw new Error('saveSavedSpot requires a user id');

  const payload = savedSpotPayload(spotInput);
  await ensureAuthedForSavedSpotWrite('saveSavedSpot');
  await setDoc(doc(db, 'users', uid, SAVED_SPOTS_COLLECTION, payload.id), payload, { merge: true });
}

export async function deleteSavedSpot(userId: string, spotId: string): Promise<void> {
  const uid = String(userId || '').trim();
  const id = String(spotId || '').trim();
  if (!uid || !id) throw new Error('deleteSavedSpot requires a user id and spot id');

  await ensureAuthedForSavedSpotWrite('deleteSavedSpot');

  // Important legacy cleanup:
  // Older Roddo builds stored saved spots on users/{uid}.savedSpots. If we only
  // delete the subcollection doc, MapView's one-time legacy migrator can see the
  // old profile array after refresh and resurrect the deleted spot. Clean the
  // legacy array first, then delete the canonical subcollection doc.
  await removeLegacyProfileSavedSpot(uid, id);
  await deleteDoc(doc(db, 'users', uid, SAVED_SPOTS_COLLECTION, id));
}

export async function migrateLegacyProfileSavedSpots(userId: string, legacySpotsInput: any[]): Promise<void> {
  const uid = String(userId || '').trim();
  if (!uid) return;

  const spots = normalizeSavedSpots(legacySpotsInput);
  if (!spots.length) return;

  await ensureAuthedForSavedSpotWrite('migrateLegacyProfileSavedSpots');
  await Promise.all(
    spots.map((spot) =>
      setDoc(doc(db, 'users', uid, SAVED_SPOTS_COLLECTION, spot.id), savedSpotPayload(spot as any), { merge: true }),
    ),
  );
}
